import { apiRequest, getApiUrl, toQueryString } from '../apiRequest';

export const organisationsMembersAPI = {
  inviteMember: async (orgId, { email, roleId }) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, roleId }),
    });
    return data?.invite ?? data;
  },
  getRoles: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/roles`, { method: 'GET' });
    return data?.roles ?? [];
  },
  createRole: async (orgId, { name, description, flagKeys }) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ name, description, flagKeys: flagKeys || [] }),
    });
    return data;
  },
  getRole: async (orgId, roleId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/roles/${encodeURIComponent(roleId)}`,
      { method: 'GET' }
    );
    return data;
  },
  updateRole: async (orgId, roleId, { name, description, flagKeys }) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/roles/${encodeURIComponent(roleId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ name, description, flagKeys }),
      }
    );
    return data;
  },
  deleteRole: async (orgId, roleId) => {
    await apiRequest<any>(`/api/organisations/${orgId}/roles/${encodeURIComponent(roleId)}`, {
      method: 'DELETE',
    });
  },
  setMemberRole: async (orgId, userId, roleId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/members/${encodeURIComponent(userId)}/role`,
      {
        method: 'PUT',
        body: JSON.stringify({ roleId: roleId || null }),
      }
    );
    return data;
  },
  getMembers: async (orgId, options: any = {}) => {
    const params: any = {};
    if (options.stats) params.stats = 'true';
    if (options.search != null && String(options.search).trim() !== '')
      params.search = String(options.search).trim();
    if (options.streamId != null && String(options.streamId).trim() !== '')
      params.streamId = String(options.streamId).trim();
    if (options.projectId != null && String(options.projectId).trim() !== '')
      params.projectId = String(options.projectId).trim();
    if (options.repositoryId != null && String(options.repositoryId).trim() !== '') {
      params.repositoryId = String(options.repositoryId).trim();
    }
    const qs = toQueryString(params);
    const data = await apiRequest<any>(`/api/organisations/${orgId}/members${qs}`, { method: 'GET' });
    return data.members ?? [];
  },
  getMemberDetail: async (orgId, userId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/members/${encodeURIComponent(userId)}`,
      { method: 'GET' }
    );
    return data;
  },
  getContributors: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/contributors`, { method: 'GET' });
    return {
      contributors: data.contributors ?? [],
      source: data.source,
      contributorsError: data.contributorsError,
    };
  },
  refreshContributors: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/contributors/refresh`, {
      method: 'POST',
    });
    return {
      contributors: data.contributors ?? [],
      source: data.source ?? 'github',
    };
  },
  linkMemberToGitHub: async (orgId, userId, { githubUsername }) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/members/${encodeURIComponent(userId)}/github-link`,
      {
        method: 'PATCH',
        body: JSON.stringify({ githubUsername }),
      }
    );
    return { linkedContributors: data.linkedContributors ?? [] };
  },
  unlinkMemberGitHub: async (orgId, userId, githubId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/members/${encodeURIComponent(userId)}/github-links/${encodeURIComponent(githubId)}`,
      { method: 'DELETE' }
    );
    return { linkedContributors: data.linkedContributors ?? [] };
  },
  deleteMember: async (orgId, userId) => {
    await apiRequest<any>(`/api/organisations/${orgId}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  },
  getInvites: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/invites`, { method: 'GET' });
    return data.invites ?? [];
  },
  deleteInvite: async (orgId, inviteId) => {
    await apiRequest<any>(`/api/organisations/${orgId}/invites/${inviteId}`, { method: 'DELETE' });
  },
  /** Matrix: distinct tech names in org (for autocomplete). */ suggestMemberGitHubLinks: async (
    orgId
  ) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/members/suggest-github-links`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return data.suggestions ?? [];
  },
  importMembersCsv: async (orgId, file) => {
    const form = new FormData();
    form.append('file', file);
    const base = getApiUrl();
    const token = localStorage.getItem('token');
    const res = await fetch(`${base}/api/organisations/${orgId}/members/import`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const raw = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(raw.message || raw.error || `Import failed (${res.status})`);
    }
    return raw.data ?? raw;
  },
};
